import Reveal from "@/components/Reveal";
import VocabTag from "@/components/VocabTag";
import { GIGA_IMAGES } from "@/lib/images";

/**
 * Schematic — Platform section.
 * Shows INTELLIGENCE and PRODUCTION as coequal halves of a single integrated
 * platform. AGI integrated with a production-ready manufacturing body.
 *   Left plate: PRODUCTION (autonomous factory floor)
 *   Right SVG: INTELLIGENCE (AGI OS topology)
 *   Panel meta: FUSED / INTEGRATED
 */
export default function Schematic() {
  return (
    <section
      id="schematic"
      data-testid="schematic-section"
      className="border-b border-[color:var(--g1-rule)]"
    >
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 lg:px-14 py-24 md:py-32 lg:py-40">
        <div className="grid grid-cols-12 gap-8 mb-12 md:mb-16 items-start">
          <div className="col-span-12 md:col-span-3 lg:col-span-2">
            <Reveal>
              <div className="g1-label">SEC. 03</div>
              <div className="g1-eyebrow mt-2">Platform</div>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-7 lg:col-span-7 lg:col-start-4">
            <Reveal delay={60}>
              <h2 className="g1-h2 text-[36px] md:text-[56px] lg:text-[72px] max-w-[22ch]">
                AGI{" "}
                <span className="g1-display-italic">integrated</span> with a
                production-ready manufacturing body.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-2 lg:col-span-2 md:col-start-11 text-right">
            <Reveal>
              <VocabTag kind="FUSED" />
            </Reveal>
          </div>
        </div>

        <Reveal delay={120}>
          <div
            data-testid="schematic-panel"
            className="relative border border-[color:var(--g1-ink)] bg-[color:var(--g1-bone)] overflow-hidden"
          >
            <div className="g1-corners">
              <span className="tl" />
              <span className="tr" />
              <span className="bl" />
              <span className="br" />
            </div>

            <div className="grid grid-cols-12">
              {/* LEFT — PRODUCTION plate */}
              <div className="col-span-12 lg:col-span-6 border-b lg:border-b-0 lg:border-r border-[color:var(--g1-rule)] relative">
                <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full min-h-[360px] overflow-hidden">
                  <img
                    src={GIGA_IMAGES.factoryFloor}
                    alt="Autonomous pharmaceutical production floor"
                    className="g1-plate-img absolute inset-0 w-full h-full"
                    loading="lazy"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(20,20,19,0.0) 0%, rgba(20,20,19,0.25) 70%, rgba(20,20,19,0.55) 100%)",
                    }}
                  />
                  <div className="absolute inset-0 p-5 md:p-7 flex flex-col justify-between pointer-events-none">
                    <div className="flex items-start justify-between">
                      <VocabTag kind="PRODUCTION" dark />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-serif italic text-xl md:text-2xl text-[color:var(--g1-bone)] max-w-[18ch]">
                        Production-ready. Embodied.
                      </span>
                      <span className="g1-label text-[color:var(--g1-bone)]/80">
                        AUTONOMOUS CELLS · LINES · RAILS
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT — INTELLIGENCE inline SVG */}
              <div
                className="col-span-12 lg:col-span-6 p-5 md:p-8 lg:p-10 relative"
                data-testid="schematic-diagram"
              >
                <div className="flex items-start justify-between mb-4 md:mb-6">
                  <VocabTag kind="INTELLIGENCE" />
                  <span className="g1-label text-[color:var(--g1-ink-3)]">
                    PLATFORM TOPOLOGY
                  </span>
                </div>
                <SchematicSVG />
                <div className="mt-4 md:mt-6 pt-3 border-t border-[color:var(--g1-rule)]">
                  <p className="font-serif italic text-xl md:text-2xl text-[color:var(--g1-ink)] max-w-[22ch]">
                    Cognitive. Integrated.
                  </p>
                  <span className="g1-label text-[color:var(--g1-ink-3)] mt-1 block">
                    AGI · CLOSED LOOP · ZERO HALLUCINATION
                  </span>
                </div>
              </div>
            </div>

            {/* Bottom meta rail */}
            <div className="grid grid-cols-12 border-t border-[color:var(--g1-rule)]">
              <div className="col-span-6 md:col-span-3 p-4 md:p-5 border-r border-[color:var(--g1-rule)] g1-label">
                PLATFORM TOPOLOGY
              </div>
              <div className="col-span-6 md:col-span-3 p-4 md:p-5 border-r border-[color:var(--g1-rule)] g1-label">
                SCOPE — AGI + PRODUCTION
              </div>
              <div className="hidden md:block md:col-span-3 p-5 border-r border-[color:var(--g1-rule)] g1-label">
                LATENCY — CLOSED LOOP
              </div>
              <div className="hidden md:block md:col-span-3 p-5 g1-label text-right">
                STATE — SHADOW → AUTONOMY
              </div>
            </div>
          </div>
        </Reveal>

        {/* Annotations */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mt-14 md:mt-20">
          {[
            ["A", "AGI OS", "A domain-specific model of the facility. Bounded by physics, chemistry, and GxP.", "INTELLIGENCE"],
            ["B", "Closed Loop", "Shadow Mode → Advisory Control → Full Autonomy. Reads, decides, acts.", "INTELLIGENCE"],
            ["C", "Production Capacity", "OSD, vials, IV/TPN, antibiotics, API — production lines owned and integrated within the platform.", "PRODUCTION"],
          ].map(([k, t, d, vocab], i) => (
            <Reveal
              key={k}
              delay={i * 80}
              className="border-t border-[color:var(--g1-ink)] pt-5"
            >
              <div className="flex items-center justify-between">
                <div className="g1-label text-[color:var(--g1-ink-3)]">
                  NODE {k}
                </div>
                <VocabTag kind={vocab} />
              </div>
              <h4 className="font-serif text-xl md:text-2xl mt-2 text-[color:var(--g1-ink)]">
                {t}
              </h4>
              <p className="font-sans text-[15px] leading-relaxed text-[color:var(--g1-ink-2)] mt-2 max-w-[36ch]">
                {d}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SchematicSVG() {
  const bodies = [
    { x: 130, label: "OSD", sub: "TABLETS" },
    { x: 360, label: "VIAL", sub: "INJECTABLE" },
    { x: 590, label: "IV / TPN", sub: "STERILE" },
    { x: 820, label: "AB", sub: "ISOLATED" },
    { x: 1050, label: "API", sub: "SYNTHESIS" },
  ];
  return (
    <svg
      viewBox="0 0 1200 620"
      className="g1-schematic w-full h-auto"
      role="img"
      aria-label="Platform topology"
    >
      <g stroke="var(--g1-rule)" strokeWidth="1">
        <line x1="0" y1="40" x2="1200" y2="40" />
        <line x1="0" y1="580" x2="1200" y2="580" />
      </g>
      <text x="8" y="28" opacity="0.55">INTELLIGENCE</text>
      <text x="8" y="600" opacity="0.55">PRODUCTION</text>
      <text x="1192" y="28" textAnchor="end" opacity="0.55">AGI</text>
      <text x="1192" y="600" textAnchor="end" opacity="0.55">LINES</text>
      <g stroke="var(--g1-rule-soft)" strokeWidth="1">
        <line x1="0" y1="120" x2="1200" y2="120" strokeDasharray="2 4" />
        <line x1="0" y1="280" x2="1200" y2="280" strokeDasharray="2 4" />
        <line x1="0" y1="440" x2="1200" y2="440" strokeDasharray="2 4" />
      </g>

      <g transform="translate(600,180)">
        <circle r="84" fill="none" stroke="var(--g1-olive)" strokeWidth="1" className="g1-schem-pulse" />
        <circle r="68" fill="var(--g1-bone)" stroke="var(--g1-ink)" strokeWidth="1.2" />
        <circle r="6" className="g1-schem-accent" />
        <text y="-96" textAnchor="middle" fontSize="11" letterSpacing="3">AGI · OS</text>
        <text y="104" textAnchor="middle" fontSize="10" letterSpacing="2" opacity="0.7">
          PHYSICS · CHEMISTRY · GxP
        </text>
      </g>

      {/* Bidirectional fusion — intelligence DOWN to production and production UP to intelligence */}
      <g stroke="var(--g1-ink)" strokeWidth="1" strokeDasharray="3 4" fill="none" opacity="0.6">
        {bodies.map((b) => (
          <path key={b.label} d={`M600,252 C600,360 ${b.x},360 ${b.x},470`} />
        ))}
      </g>
      <g stroke="var(--g1-olive)" strokeWidth="1" strokeDasharray="1 3" fill="none" opacity="0.55">
        {bodies.map((b) => (
          <path key={`r-${b.label}`} d={`M${b.x},470 C${b.x},360 600,360 600,252`} />
        ))}
      </g>

      {bodies.map((b, i) => (
        <g key={b.label} transform={`translate(${b.x},500)`}>
          <rect x="-48" y="-22" width="96" height="44" fill="var(--g1-bone)" stroke="var(--g1-ink)" strokeWidth="1" />
          <text y="-4" textAnchor="middle" fontSize="10" letterSpacing="2">{b.label}</text>
          <text y="12" textAnchor="middle" fontSize="8" letterSpacing="2" opacity="0.55">{b.sub}</text>
          <text y="50" textAnchor="middle" fontSize="8" letterSpacing="2" opacity="0.45">
            {`LINE ${String(i + 1).padStart(2, "0")}`}
          </text>
        </g>
      ))}
    </svg>
  );
}
